import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { showConfirmModal } from '../components/PopupModal';

interface NavigationBlockerContextType {
  shouldBlock: () => boolean;
  registerBlocker: (id: string, shouldBlock: boolean, returnPath: string) => void;
  unregisterBlocker: (id: string) => void;
  checkAndConfirmNavigation: (targetPath: string, onConfirm: () => void) => void;
  isNavigating: () => boolean;
  clearAllBlockers: () => void;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextType | undefined>(undefined);

interface BlockerInfo {
  shouldBlock: boolean;
  returnPath: string;
}

export const NavigationBlockerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [blockers, setBlockers] = useState<Map<string, BlockerInfo>>(new Map());
  const navigatingRef = useRef(false);

  const registerBlocker = useCallback((id: string, shouldBlock: boolean, returnPath: string) => {
    setBlockers(prev => {
      const next = new Map(prev);
      next.set(id, { shouldBlock, returnPath });
      return next;
    });
  }, []);

  const unregisterBlocker = useCallback((id: string) => {
    setBlockers(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const shouldBlock = useCallback(() => {
    // 네비게이션 진행 중이면 차단하지 않음
    if (navigatingRef.current) {
      return false;
    }
    const blockersArray = Array.from(blockers.values());
    for (const info of blockersArray) {
      if (info.shouldBlock) {
        return true;
      }
    }
    return false;
  }, [blockers]);

  const isNavigating = useCallback(() => {
    return navigatingRef.current;
  }, []);

  const clearAllBlockers = useCallback(() => {
    navigatingRef.current = true;
    setBlockers(new Map());
    setTimeout(() => {
      navigatingRef.current = false;
    }, 100);
  }, []);

  const checkAndConfirmNavigation = useCallback((targetPath: string, onConfirm: () => void) => {
    if (shouldBlock()) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          // 네비게이션 진행 플래그 설정
          navigatingRef.current = true;
          // 모든 blocker를 비활성화하고 네비게이션 진행
          setBlockers(new Map());
          onConfirm();
          // 네비게이션 완료 후 플래그 해제 (약간의 지연)
          setTimeout(() => {
            navigatingRef.current = false;
          }, 100);
        },
      });
    } else {
      onConfirm();
    }
  }, [shouldBlock]);

  return (
    <NavigationBlockerContext.Provider value={{ shouldBlock, registerBlocker, unregisterBlocker, checkAndConfirmNavigation, isNavigating, clearAllBlockers }}>
      {children}
    </NavigationBlockerContext.Provider>
  );
};

export const useNavigationBlockerContext = () => {
  const context = useContext(NavigationBlockerContext);
  if (!context) {
    throw new Error('useNavigationBlockerContext must be used within NavigationBlockerProvider');
  }
  return context;
};
